import type { Router, RouteRecordRaw } from 'vue-router'

/**
 * 餐饮独立窗隐藏页挂载约定
 *
 * 总裁驾驶舱快捷入口（积分管理 / 文档帮助）依赖 point/list、content/article 等叶子路由。
 * 静态桩 standalone-apps 在后台菜单注入后可能被同名父级覆盖或抢匹配，导致 404 / 目录无子页报错。
 * 菜单树就绪后把隐藏子路由挂到所有 /ai-Application-center/catering（及 /catering）Layout 父级上。
 */

const CATERING_ROOT_PATHS = ['/ai-Application-center/catering', '/catering']

type CateringHiddenChild = {
  path: string
  name: string
  component?: RouteRecordRaw['component']
  redirect?: string
  meta: Record<string, unknown>
}

const CATERING_HIDDEN_CHILDREN: CateringHiddenChild[] = [
  {
    path: 'content/banner',
    name: 'CateringHomeBannerHidden',
    component: () => import('@/views/catering/home-banner/index.vue'),
    meta: { hidden: true, canTo: true, title: '首页轮播图' }
  },
  {
    path: 'home-banner',
    name: 'CateringHomeBannerAliasHidden',
    component: () => import('@/views/catering/home-banner/index.vue'),
    meta: { hidden: true, canTo: true, title: '首页轮播图' }
  },
  {
    path: 'point',
    name: 'CateringPointDirRedirectHidden',
    redirect: '/ai-Application-center/catering/point/list',
    meta: { hidden: true, canTo: true }
  },
  {
    path: 'point/list',
    name: 'CateringPointListHidden',
    component: () => import('@/views/catering/point/index.vue'),
    meta: {
      hidden: true,
      canTo: true,
      title: '积分管理',
      activeMenu: '/ai-Application-center/catering/point/list'
    }
  },
  {
    path: 'point/setting',
    name: 'CateringPointSettingHidden',
    component: () => import('@/views/catering/point/index.vue'),
    meta: { hidden: true, canTo: true, title: '积分设置' }
  },
  {
    path: 'article',
    name: 'CateringArticleAliasRedirectHidden',
    redirect: '/ai-Application-center/catering/content/article',
    meta: { hidden: true, canTo: true }
  },
  {
    path: 'content/article',
    name: 'CateringArticleHidden',
    component: () => import('@/views/catering/article/index.vue'),
    meta: {
      hidden: true,
      canTo: true,
      title: '文档帮助',
      activeMenu: '/ai-Application-center/catering/content/article'
    }
  },
  {
    path: 'content/article/index',
    name: 'CateringArticleIndexHidden',
    component: () => import('@/views/catering/article/index.vue'),
    meta: {
      hidden: true,
      canTo: true,
      title: '文档帮助',
      activeMenu: '/ai-Application-center/catering/content/article'
    }
  },
  {
    path: 'order',
    name: 'CateringOrderDirRedirectHidden',
    redirect: '/ai-Application-center/catering/order/list',
    meta: { hidden: true, canTo: true }
  },
  {
    path: 'order/list',
    name: 'CateringOrderListHidden',
    component: () => import('@/views/catering/order/index.vue'),
    meta: {
      hidden: true,
      canTo: true,
      title: '订单列表',
      activeMenu: '/ai-Application-center/catering/order/list'
    }
  },
  {
    path: 'order/refund',
    name: 'CateringOrderRefundHidden',
    component: () => import('@/views/catering/refund/index.vue'),
    meta: {
      hidden: true,
      canTo: true,
      title: '退单/售后',
      activeMenu: '/ai-Application-center/catering/order/refund'
    }
  }
]

const ALL_HIDDEN_NAMES = CATERING_HIDDEN_CHILDREN.flatMap((c) => [c.name, `${c.name}AiAppCenter`])

const registeredCateringHiddenNames = new Set<string>()

function parentHasChildPath(router: Router, parentName: string, childPath: string): boolean {
  const parent = router.getRoutes().find((r) => r.name === parentName)
  if (!parent?.children?.length) return false
  return parent.children.some((c) => String(c.path || '') === childPath)
}

/** 优先挂到后台菜单注入的餐饮 Layout（子路由更多），避免静态桩父级抢匹配 */
function findCateringLayoutParentNames(router: Router, rootPath: string): string[] {
  const roots = router.getRoutes().filter((r) => r.path === rootPath && r.name)
  if (!roots.length) return []

  const menuHint = (p: string) =>
    p === 'cockpit' ||
    p === 'dashboard' ||
    p === 'order' ||
    p.startsWith('order/') ||
    p === 'product' ||
    p.startsWith('product/') ||
    p === 'point' ||
    p.startsWith('point/') ||
    p === 'content' ||
    p.startsWith('content/')

  const ranked = [...roots].sort((a, b) => {
    const aMenu = (a.children || []).some((c) => menuHint(String(c.path || ''))) ? 1 : 0
    const bMenu = (b.children || []).some((c) => menuHint(String(c.path || ''))) ? 1 : 0
    if (bMenu !== aMenu) return bMenu - aMenu
    return (b.children?.length ?? 0) - (a.children?.length ?? 0)
  })
  return ranked.map((r) => String(r.name))
}

function toChildRecord(def: CateringHiddenChild, name: string): RouteRecordRaw {
  const record: RouteRecordRaw = {
    path: def.path,
    name,
    meta: { ...def.meta }
  }
  if (def.redirect) record.redirect = def.redirect
  if (def.component) record.component = def.component
  return record
}

/**
 * 后台餐饮菜单树就绪后调用：把总裁驾驶舱快捷入口依赖的隐藏页挂到餐饮 Layout 父级。
 */
export function ensureCateringHiddenRoutes(router: Router) {
  for (const name of [...registeredCateringHiddenNames, ...ALL_HIDDEN_NAMES]) {
    if (router.hasRoute(name)) {
      router.removeRoute(name)
    }
  }
  registeredCateringHiddenNames.clear()

  for (const rootPath of CATERING_ROOT_PATHS) {
    const parents = findCateringLayoutParentNames(router, rootPath)
    parents.forEach((parentName, index) => {
      for (const def of CATERING_HIDDEN_CHILDREN) {
        if (parentHasChildPath(router, parentName, def.path)) {
          continue
        }
        const name =
          rootPath === '/ai-Application-center/catering'
            ? index === 0
              ? def.name
              : `${def.name}__${parentName}`
            : index === 0
              ? `${def.name}Bare`
              : `${def.name}Bare__${parentName}`
        router.addRoute(parentName, toChildRecord(def, name))
        registeredCateringHiddenNames.add(name)
      }
    })
  }
}
